import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";

interface PipelineStage {
  name: string;
  color: string;
}

interface Automation {
  name: string;
  trigger: string;
}

interface Template {
  name: string;
  type: "page" | "email";
}

interface FormItem {
  name: string;
  fieldCount: number;
}

interface OnboardingConfig {
  pipeline: PipelineStage[];
  automations: Automation[];
  templates: Template[];
  forms: FormItem[];
}

const STAGE_COLORS: string[] = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];
const DEFAULT_STAGE_COLOR = "#3B82F6";

export default function ConfigPreviewPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<{ section: string; index: number } | null>(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    apiFetch<OnboardingConfig>("/api/onboarding/config-preview")
      .then(setConfig)
      .catch(() => {
        setConfig({
          pipeline: [
            { name: "New Lead", color: STAGE_COLORS[0] ?? DEFAULT_STAGE_COLOR },
            { name: "Contacted", color: STAGE_COLORS[1] ?? DEFAULT_STAGE_COLOR },
            { name: "Qualified", color: STAGE_COLORS[2] ?? DEFAULT_STAGE_COLOR },
            { name: "Proposal", color: STAGE_COLORS[3] ?? DEFAULT_STAGE_COLOR },
            { name: "Won", color: STAGE_COLORS[4] ?? DEFAULT_STAGE_COLOR },
          ],
          automations: [
            { name: "Welcome Email", trigger: "New contact created" },
            { name: "Follow-Up Reminder", trigger: "No response after 3 days" },
            { name: "Review Request", trigger: "Service completed" },
          ],
          templates: [
            { name: "Landing Page", type: "page" },
            { name: "Booking Confirmation", type: "email" },
            { name: "Welcome Series", type: "email" },
          ],
          forms: [
            { name: "Contact Form", fieldCount: 5 },
            { name: "Booking Request", fieldCount: 8 },
          ],
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
    setEditingItem(null);
  };

  const startEditing = (section: string, index: number, currentName: string) => {
    setEditingItem({ section, index });
    setEditValue(currentName);
  };

  const commitEdit = useCallback(() => {
    if (!editingItem || !config || !editValue.trim()) {
      setEditingItem(null);
      return;
    }
    const { section, index } = editingItem;
    const trimmed = editValue.trim();
    const updated = { ...config };
    if (section === "pipeline") {
      const item = config.pipeline[index];
      if (item) updated.pipeline = config.pipeline.map((s, i) => (i === index ? { ...s, name: trimmed } : s));
    } else if (section === "automations") {
      const item = config.automations[index];
      if (item) updated.automations = config.automations.map((a, i) => (i === index ? { ...a, name: trimmed } : a));
    } else if (section === "templates") {
      const item = config.templates[index];
      if (item) updated.templates = config.templates.map((t, i) => (i === index ? { ...t, name: trimmed } : t));
    } else if (section === "forms") {
      const item = config.forms[index];
      if (item) updated.forms = config.forms.map((f, i) => (i === index ? { ...f, name: trimmed } : f));
    }
    setConfig(updated);
    setEditingItem(null);
  }, [editingItem, config, editValue]);

  const deleteItem = (section: string, index: number) => {
    if (!config) return;
    const updated = { ...config };
    if (section === "pipeline") updated.pipeline = config.pipeline.filter((_, i) => i !== index);
    if (section === "automations") updated.automations = config.automations.filter((_, i) => i !== index);
    if (section === "templates") updated.templates = config.templates.filter((_, i) => i !== index);
    if (section === "forms") updated.forms = config.forms.filter((_, i) => i !== index);
    setConfig(updated);
  };

  const handleApply = async () => {
    if (!config) return;
    setIsApplying(true);
    try {
      await apiFetch("/api/onboarding/config-preview/apply", {
        method: "POST",
        body: JSON.stringify(config),
      });
      navigate("/dashboard", { replace: true });
    } finally {
      setIsApplying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" aria-busy="true" role="status">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
          <span className="text-muted-foreground">Loading configuration...</span>
        </div>
      </div>
    );
  }

  if (!config) return null;

  const sections = [
    { key: "pipeline", label: "Pipeline Stages", count: config.pipeline.length },
    { key: "automations", label: "Automations", count: config.automations.length },
    { key: "templates", label: "Templates", count: config.templates.length },
    { key: "forms", label: "Forms", count: config.forms.length },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="mx-auto w-full max-w-2xl px-6 py-8">
        <h1 className="text-2xl font-bold text-foreground">Review your configuration</h1>
        <p className="mt-1 text-muted-foreground">
          Everything below will be created for your account. Edit or remove items before applying.
        </p>

        {/* Summary cards */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {sections.map((s) => (
            <div key={s.key} className="rounded-lg border border-border p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{s.count}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Accordion sections */}
        <div className="mt-6 space-y-3">
          {/* Pipeline */}
          <AccordionSection
            title="Pipeline Stages"
            count={config.pipeline.length}
            isOpen={expandedSection === "pipeline"}
            onToggle={() => toggleSection("pipeline")}
          >
            {config.pipeline.map((stage, i) => (
              <ItemRow
                key={i}
                isEditing={editingItem?.section === "pipeline" && editingItem.index === i}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onStartEdit={() => startEditing("pipeline", i, stage.name)}
                onCommitEdit={commitEdit}
                onDelete={() => deleteItem("pipeline", i)}
              >
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />
                <span className="text-sm text-foreground">{stage.name}</span>
              </ItemRow>
            ))}
          </AccordionSection>

          {/* Automations */}
          <AccordionSection
            title="Automations"
            count={config.automations.length}
            isOpen={expandedSection === "automations"}
            onToggle={() => toggleSection("automations")}
          >
            {config.automations.map((auto, i) => (
              <ItemRow
                key={i}
                isEditing={editingItem?.section === "automations" && editingItem.index === i}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onStartEdit={() => startEditing("automations", i, auto.name)}
                onCommitEdit={commitEdit}
                onDelete={() => deleteItem("automations", i)}
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{auto.name}</p>
                  <p className="text-xs text-muted-foreground">{auto.trigger}</p>
                </div>
              </ItemRow>
            ))}
          </AccordionSection>

          {/* Templates */}
          <AccordionSection
            title="Templates"
            count={config.templates.length}
            isOpen={expandedSection === "templates"}
            onToggle={() => toggleSection("templates")}
          >
            {config.templates.map((tpl, i) => (
              <ItemRow
                key={i}
                isEditing={editingItem?.section === "templates" && editingItem.index === i}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onStartEdit={() => startEditing("templates", i, tpl.name)}
                onCommitEdit={commitEdit}
                onDelete={() => deleteItem("templates", i)}
              >
                <span className="text-sm text-foreground">{tpl.name}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    tpl.type === "page"
                      ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] dark:bg-[var(--color-primary)]/30 dark:text-[var(--color-primary)]/70"
                      : "bg-[var(--color-success)]/10 text-[var(--color-success)] dark:bg-[var(--color-success)]/30 dark:text-[var(--color-success)]"
                  }`}
                >
                  {tpl.type}
                </span>
              </ItemRow>
            ))}
          </AccordionSection>

          {/* Forms */}
          <AccordionSection
            title="Forms"
            count={config.forms.length}
            isOpen={expandedSection === "forms"}
            onToggle={() => toggleSection("forms")}
          >
            {config.forms.map((form, i) => (
              <ItemRow
                key={i}
                isEditing={editingItem?.section === "forms" && editingItem.index === i}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onStartEdit={() => startEditing("forms", i, form.name)}
                onCommitEdit={commitEdit}
                onDelete={() => deleteItem("forms", i)}
              >
                <span className="text-sm text-foreground">{form.name}</span>
                <span className="text-xs text-muted-foreground">{form.fieldCount} fields</span>
              </ItemRow>
            ))}
          </AccordionSection>
        </div>

        {/* Actions */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Back to Edit
          </button>
          <button
            onClick={handleApply}
            disabled={isApplying}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isApplying ? "Applying..." : "Apply Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AccordionSection({
  title,
  count,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const contentId = `${title.toLowerCase().replace(/\s+/g, "-")}-content`;

  return (
    <div className="rounded-lg border border-border">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <span className="text-sm font-semibold text-foreground">
          {title} ({count})
        </span>
        <span className="text-muted-foreground" aria-hidden="true">{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && <div id={contentId} className="border-t border-border px-4 py-2">{children}</div>}
    </div>
  );
}

function ItemRow({
  children,
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onCommitEdit,
  onDelete,
}: {
  children: React.ReactNode;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (v: string) => void;
  onStartEdit: () => void;
  onCommitEdit: () => void;
  onDelete: () => void;
}) {
  const [isRowActive, setIsRowActive] = useState(false);

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 py-2">
        <input
          autoFocus
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onCommitEdit()}
          onBlur={onCommitEdit}
          className="flex-1 rounded border border-primary bg-background px-2 py-1 text-sm text-foreground"
          aria-label="Edit configuration item name"
        />
      </div>
    );
  }

  return (
    <div
      className="group flex items-center justify-between py-2"
      onClick={() => setIsRowActive(!isRowActive)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsRowActive(false);
        }
      }}
    >
      <div className="flex items-center gap-2">{children}</div>
      <div className={`flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${isRowActive ? "opacity-100" : ""}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onStartEdit();
          }}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          title="Edit"
          aria-label="Edit configuration item"
        >
          <PencilIcon />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          title="Delete"
          aria-label="Delete configuration item"
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}
